import { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Flame, CheckCircle2, XCircle, MinusCircle, Calendar, Clock, X, Trash2, Info } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, startOfDay, addMonths, subMonths, parseISO } from 'date-fns';
import { nl, fr, enUS } from 'date-fns/locale';
import { getLocalizedString } from '@/i18n/translations';
import { Habit, HabitTemplate } from '@/types';

const DAY_MAP: Record<number, string> = {
  0: 'zo', 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za',
};

const WEEKDAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const WEEKDAYS_FR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const WEEKDAYS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

type DayStatus = 'complete' | 'partial' | 'missed' | 'too-late' | 'skipped' | 'future' | 'no-habits';

export default function TrackerPage() {
  const { settings, progress, templates } = useStore();
  const lang = settings.language;
  const locale = lang === 'nl' ? nl : lang === 'fr' ? fr : enUS;
  const weekdays = lang === 'nl' ? WEEKDAYS_NL : lang === 'fr' ? WEEKDAYS_FR : WEEKDAYS_EN;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize from URL param if present
  const initialDate = searchParams.get('date');
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialDate) {
      try {
        return parseISO(initialDate);
      } catch {
        return new Date();
      }
    }
    return new Date();
  });
  const [selectedDay, setSelectedDay] = useState<Date | null>(() => {
    if (initialDate) {
      try {
        return parseISO(initialDate);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [infoTemplate, setInfoTemplate] = useState<HabitTemplate | null>(null);
  const [infoHabit, setInfoHabit] = useState<Habit | null>(null);

  const selectedTemplates = useMemo(
    () => templates.filter((t) => (progress.selectedTemplates || []).includes(t.id)),
    [templates, progress.selectedTemplates]
  );

  const allHabits = useMemo(
    () => selectedTemplates.flatMap((t) => t.habits).filter((h) => !progress.customHabits?.[h.id]?.disabled),
    [selectedTemplates, progress.customHabits]
  );

  // All habits including paused (for showing in day details)
  const allHabitsIncludingPaused = useMemo(
    () => selectedTemplates.flatMap((t) => t.habits),
    [selectedTemplates]
  );

  // Get habits scheduled for a specific date
  const getScheduledHabits = (date: Date) => {
    const dayCode = DAY_MAP[date.getDay()];
    return allHabits.filter((habit) => {
      const custom = progress.customHabits?.[habit.id];
      const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
      if (isOneTime) return false; // One-time tasks handled separately
      const effectiveDays = custom?.days || habit.days || ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
      return effectiveDays.includes(dayCode);
    });
  };

  // Get day status
  const getDayStatus = (date: Date): DayStatus => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const today = startOfDay(new Date());

    // Future days
    if (date > today) return 'future';

    const scheduled = getScheduledHabits(date);

    // Check if there's any data for this day (completions or skips)
    const hasCompletions = progress.completions.some((c) => c.date === dateStr);
    const hasSkips = Object.values(progress.skippedHabits || {}).some((dates) => dates.includes(dateStr));
    const hasData = hasCompletions || hasSkips;

    // Before start date AND no data - don't show
    if (progress.startDate && date < new Date(progress.startDate) && !hasData) return 'no-habits';

    if (scheduled.length === 0 && !hasData) return 'no-habits';

    const completedIds = progress.completions
      .filter((c) => c.date === dateStr && c.completed)
      .map((c) => c.habitId);

    const skippedIds = Object.entries(progress.skippedHabits || {})
      .filter(([_, dates]) => dates.includes(dateStr))
      .map(([habitId]) => habitId);

    const completedCount = scheduled.filter((h) => completedIds.includes(h.id)).length;
    const skippedCount = scheduled.filter((h) => skippedIds.includes(h.id)).length;

    // Calculate days ago
    const daysAgo = Math.floor((today.getTime() - startOfDay(date).getTime()) / (1000 * 60 * 60 * 24));

    if (completedCount === scheduled.length) return 'complete';
    if (skippedCount === scheduled.length) return 'skipped';
    if (completedCount > 0 || skippedCount > 0) return 'partial';

    // Today with nothing done yet = still time (show as partial/in-progress)
    if (daysAgo === 0) return 'partial';

    // Yesterday with nothing done = missed
    if (daysAgo === 1) return 'missed';

    // "Too late" for days 2+ days ago with nothing done
    return 'too-late';
  };

  // Get completion stats for a day
  const getDayStats = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const scheduled = getScheduledHabits(date);

    const completedIds = progress.completions
      .filter((c) => c.date === dateStr && c.completed)
      .map((c) => c.habitId);

    const skippedIds = Object.entries(progress.skippedHabits || {})
      .filter(([_, dates]) => dates.includes(dateStr))
      .map(([habitId]) => habitId);

    return {
      total: scheduled.length,
      completed: scheduled.filter((h) => completedIds.includes(h.id)).length,
      skipped: scheduled.filter((h) => skippedIds.includes(h.id)).length,
    };
  };

  // Get detailed habits for a specific day, grouped by template
  const getDayHabitsGrouped = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayCode = DAY_MAP[date.getDay()];
    const today = startOfDay(new Date());
    const daysAgo = Math.floor((today.getTime() - startOfDay(date).getTime()) / (1000 * 60 * 60 * 24));

    const completedIds = progress.completions
      .filter((c) => c.date === dateStr && c.completed)
      .map((c) => c.habitId);

    const skippedIds = Object.entries(progress.skippedHabits || {})
      .filter(([_, dates]) => dates.includes(dateStr))
      .map(([habitId]) => habitId);

    // Group habits by template
    return selectedTemplates.map((template) => {
      const habits = template.habits
        .filter((habit) => {
          const custom = progress.customHabits?.[habit.id];
          const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
          if (isOneTime) return false;
          const effectiveDays = custom?.days || habit.days || ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
          return effectiveDays.includes(dayCode);
        })
        .map((habit) => {
          const isPaused = progress.customHabits?.[habit.id]?.disabled;
          let status: 'completed' | 'skipped' | 'missed' | 'todo' | 'paused';

          if (isPaused) {
            status = 'paused';
          } else if (completedIds.includes(habit.id)) {
            status = 'completed';
          } else if (skippedIds.includes(habit.id)) {
            status = 'skipped';
          } else if (daysAgo === 0) {
            status = 'todo';
          } else {
            status = 'missed';
          }

          return { habit, status, isPaused };
        });

      return { template, habits };
    }).filter((group) => group.habits.length > 0); // Only show templates with scheduled habits
  };

  // Calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start of month to Monday
  const startDay = monthStart.getDay();
  const paddingDays = startDay === 0 ? 6 : startDay - 1; // Monday = 0 padding

  // Monthly stats
  const monthlyStats = useMemo(() => {
    const today = startOfDay(new Date());
    let totalScheduled = 0;
    let totalCompleted = 0;
    let totalSkipped = 0;
    let perfectDays = 0;
    let activeDays = 0;

    daysInMonth.forEach((day) => {
      if (day > today) return; // Skip future days
      if (progress.startDate && day < new Date(progress.startDate)) return;

      const stats = getDayStats(day);
      if (stats.total === 0) return;

      activeDays++;
      totalScheduled += stats.total;
      totalCompleted += stats.completed;
      totalSkipped += stats.skipped;

      if (stats.completed === stats.total) {
        perfectDays++;
      }
    });

    const completionRate = totalScheduled > 0 ? Math.round((totalCompleted / totalScheduled) * 100) : 0;

    return {
      totalScheduled,
      totalCompleted,
      totalSkipped,
      perfectDays,
      activeDays,
      completionRate,
    };
  }, [daysInMonth, progress.completions, progress.skippedHabits, progress.startDate, allHabits]);

  const goToPrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToCurrentMonth = () => setCurrentMonth(new Date());

  const isCurrentMonth = isSameMonth(currentMonth, new Date());

  return (
    <div className="min-h-screen pb-24 px-4 py-6">
      {/* Month Navigation */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPrevMonth}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="text-center">
            <button
              onClick={() => setSelectedDay(null)}
              className="text-lg font-semibold text-gray-900 capitalize hover:text-primary-600 transition-colors"
            >
              {format(currentMonth, 'MMMM yyyy', { locale })}
            </button>
            {!isCurrentMonth && (
              <button
                onClick={goToCurrentMonth}
                className="block mx-auto text-xs text-primary-600 hover:text-primary-700"
              >
                {lang === 'nl' ? 'Terug naar deze maand' : lang === 'fr' ? 'Retour à ce mois' : 'Back to this month'}
              </button>
            )}
          </div>

          <button
            onClick={goToNextMonth}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekdays.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Padding for start of month */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`pad-${i}`} className="aspect-square" />
          ))}

          {/* Days */}
          {daysInMonth.map((day) => {
            const status = getDayStatus(day);
            const stats = getDayStats(day);
            const isCurrentDay = isToday(day);

            // Only allow clicking on yesterday and today (not older)
            const clickableDaysAgo = Math.floor((startOfDay(new Date()).getTime() - startOfDay(day).getTime()) / (1000 * 60 * 60 * 24));
            const isClickable = status !== 'future' && status !== 'no-habits' && clickableDaysAgo <= 1;

            return (
              <button
                key={day.toISOString()}
                onClick={() => isClickable && setSelectedDay(day)}
                disabled={!isClickable}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-colors ${
                  isCurrentDay ? 'ring-2 ring-primary-500 ring-offset-1' : ''
                } ${
                  isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-60'
                } ${
                  status === 'complete'
                    ? 'bg-emerald-100 text-emerald-700'
                    : status === 'partial'
                    ? 'bg-amber-100 text-amber-700'
                    : status === 'missed'
                    ? 'bg-red-100 text-red-700'
                    : status === 'too-late'
                    ? 'bg-slate-200 text-slate-500'
                    : status === 'skipped'
                    ? 'bg-gray-100 text-gray-500'
                    : status === 'future'
                    ? 'bg-gray-50 text-gray-400'
                    : 'bg-white text-gray-400'
                }`}
                title={stats.total > 0 ? `${stats.completed}/${stats.total}` : ''}
              >
                <span className={`font-medium ${isCurrentDay ? 'text-primary-600' : ''}`}>
                  {format(day, 'd')}
                </span>
                {status === 'complete' && <CheckCircle2 className="w-3 h-3 mt-0.5" />}
                {status === 'partial' && (
                  <span className="text-[10px] mt-0.5">{stats.completed}/{stats.total}</span>
                )}
                {status === 'missed' && <XCircle className="w-3 h-3 mt-0.5" />}
                {status === 'too-late' && <Clock className="w-3 h-3 mt-0.5" />}
                {status === 'skipped' && <MinusCircle className="w-3 h-3 mt-0.5" />}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4 pt-4 border-t border-gray-100 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-emerald-100" />
            <span className="text-gray-600">{lang === 'nl' ? 'Compleet' : lang === 'fr' ? 'Complet' : 'Complete'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-100" />
            <span className="text-gray-600">{lang === 'nl' ? 'Deels' : lang === 'fr' ? 'Partiel' : 'Partial'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-100" />
            <span className="text-gray-600">{lang === 'nl' ? 'Gemist' : lang === 'fr' ? 'Manqué' : 'Missed'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-slate-200" />
            <span className="text-gray-600">{lang === 'nl' ? 'Te laat' : lang === 'fr' ? 'Trop tard' : 'Too late'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-100" />
            <span className="text-gray-600">{lang === 'nl' ? 'Overgeslagen' : lang === 'fr' ? 'Passé' : 'Skipped'}</span>
          </div>
        </div>
      </div>

      {/* Day Details Panel */}
      {selectedDay && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 capitalize">
              {format(selectedDay, 'EEEE d MMMM', { locale })}
            </h3>
            <button
              onClick={() => setSelectedDay(null)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {getDayHabitsGrouped(selectedDay).length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              {lang === 'nl' ? 'Geen gewoontes gepland' : lang === 'fr' ? 'Aucune habitude prévue' : 'No habits scheduled'}
            </p>
          ) : (
            <div className="space-y-4">
              {getDayHabitsGrouped(selectedDay).map(({ template, habits }) => {
                const templateName = template.name[lang] || template.name.en;

                return (
                  <div key={template.id}>
                    {/* Template Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{template.icon}</span>
                      <h4 className="font-medium text-gray-700">{templateName}</h4>
                      <button
                        onClick={() => setInfoTemplate(template)}
                        className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                        title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Habits in this template */}
                    <div className="space-y-2 ml-1">
                      {habits.map(({ habit, status }) => {
                        const habitName = habit.name[lang] || habit.name.en;

                        return (
                          <div
                            key={habit.id}
                            className={`flex items-center gap-3 p-3 rounded-xl ${
                              status === 'completed'
                                ? 'bg-emerald-50'
                                : status === 'skipped'
                                ? 'bg-gray-50'
                                : status === 'todo'
                                ? 'bg-amber-50'
                                : status === 'paused'
                                ? 'bg-slate-50'
                                : 'bg-red-50'
                            }`}
                          >
                            {status === 'completed' ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            ) : status === 'skipped' ? (
                              <MinusCircle className="w-5 h-5 text-gray-400" />
                            ) : status === 'todo' ? (
                              <Clock className="w-5 h-5 text-amber-500" />
                            ) : status === 'paused' ? (
                              <MinusCircle className="w-5 h-5 text-slate-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                            <span className="text-lg">{habit.icon}</span>
                            <span className={`flex-1 ${
                              status === 'completed' ? 'text-gray-700'
                                : status === 'skipped' ? 'text-gray-500'
                                : status === 'todo' ? 'text-amber-700'
                                : status === 'paused' ? 'text-slate-400'
                                : 'text-red-700'
                            }`}>
                              {habitName}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setInfoHabit(habit);
                              }}
                              className="p-0.5 rounded text-gray-300 hover:text-primary-500 transition-colors"
                              title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : status === 'skipped'
                                ? 'bg-gray-100 text-gray-600'
                                : status === 'todo'
                                ? 'bg-amber-100 text-amber-700'
                                : status === 'paused'
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {status === 'completed'
                                ? (lang === 'nl' ? 'Voltooid' : lang === 'fr' ? 'Terminé' : 'Done')
                                : status === 'skipped'
                                ? (lang === 'nl' ? 'Overgeslagen' : lang === 'fr' ? 'Passé' : 'Skipped')
                                : status === 'todo'
                                ? (lang === 'nl' ? 'Te doen' : lang === 'fr' ? 'À faire' : 'Todo')
                                : status === 'paused'
                                ? (lang === 'nl' ? 'Gepauzeerd' : lang === 'fr' ? 'En pause' : 'Paused')
                                : (lang === 'nl' ? 'Gemist' : lang === 'fr' ? 'Manqué' : 'Missed')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Day summary */}
          {getDayHabitsGrouped(selectedDay).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
              {(() => {
                const groups = getDayHabitsGrouped(selectedDay);
                const allHabits = groups.flatMap((g) => g.habits);
                // Don't count paused habits in the total
                const activeHabits = allHabits.filter((h) => h.status !== 'paused');
                const completed = activeHabits.filter((h) => h.status === 'completed').length;
                const total = activeHabits.length;
                return `${completed}/${total} ${lang === 'nl' ? 'voltooid' : lang === 'fr' ? 'terminé' : 'completed'}`;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Monthly Stats */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-500 mb-4">
          {lang === 'nl' ? 'Statistieken deze maand' : lang === 'fr' ? 'Statistiques ce mois' : 'This month\'s stats'}
        </h3>

        <div className="grid grid-cols-2 gap-4">
          {/* Completion Rate */}
          <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-xl p-4">
            <div className="text-3xl font-bold text-primary-600">{monthlyStats.completionRate}%</div>
            <div className="text-xs text-gray-600">
              {lang === 'nl' ? 'Voltooiingsgraad' : lang === 'fr' ? 'Taux de complétion' : 'Completion rate'}
            </div>
          </div>

          {/* Perfect Days */}
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4">
            <div className="text-3xl font-bold text-emerald-600">{monthlyStats.perfectDays}</div>
            <div className="text-xs text-gray-600">
              {lang === 'nl' ? 'Perfecte dagen' : lang === 'fr' ? 'Jours parfaits' : 'Perfect days'}
            </div>
          </div>

          {/* Total Completed */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4">
            <div className="text-3xl font-bold text-blue-600">{monthlyStats.totalCompleted}</div>
            <div className="text-xs text-gray-600">
              {lang === 'nl' ? 'Gewoontes voltooid' : lang === 'fr' ? 'Habitudes terminées' : 'Habits completed'}
            </div>
          </div>

          {/* Active Days */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
            <div className="text-3xl font-bold text-purple-600">{monthlyStats.activeDays}</div>
            <div className="text-xs text-gray-600">
              {lang === 'nl' ? 'Actieve dagen' : lang === 'fr' ? 'Jours actifs' : 'Active days'}
            </div>
          </div>
        </div>

        {/* Current Streak */}
        {progress.streak > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="text-lg font-semibold text-orange-500">
              {progress.streak} {lang === 'nl' ? 'dagen streak' : lang === 'fr' ? 'jours de suite' : 'day streak'}
            </span>
          </div>
        )}
      </div>

      {/* No habits message */}
      {selectedTemplates.length === 0 && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-6 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {lang === 'nl'
              ? 'Selecteer eerst een habit template om je voortgang te zien.'
              : lang === 'fr'
              ? 'Sélectionnez d\'abord un modèle d\'habitude pour voir vos progrès.'
              : 'Select a habit template first to see your progress.'}
          </p>
        </div>
      )}

      {/* Template Info Popup */}
      {infoTemplate && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setInfoTemplate(null)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] overflow-y-auto shadow-xl mb-20 sm:mb-0">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{infoTemplate.icon}</span>
                <span className="font-semibold text-gray-900">
                  {infoTemplate.name[lang] || infoTemplate.name.en}
                </span>
              </div>
              <button
                onClick={() => setInfoTemplate(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {infoTemplate.description && (
                <p className="text-gray-600">
                  {infoTemplate.description[lang] || infoTemplate.description.en}
                </p>
              )}
              <div className="text-sm text-gray-500">
                {infoTemplate.habits.length} {lang === 'nl' ? 'gewoontes' : lang === 'fr' ? 'habitudes' : 'habits'}
              </div>
              {infoTemplate.tips && infoTemplate.tips.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-3">
                  <div className="text-sm font-medium text-amber-700 mb-2">
                    💡 {lang === 'nl' ? 'Tips' : lang === 'fr' ? 'Conseils' : 'Tips'}
                  </div>
                  <ul className="text-sm text-amber-600 space-y-1">
                    {infoTemplate.tips.map((tip, i) => (
                      <li key={i}>• {tip[lang] || tip.en}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Habit Info Popup */}
      {infoHabit && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setInfoHabit(null)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] overflow-y-auto shadow-xl mb-20 sm:mb-0">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{infoHabit.icon}</span>
                <span className="font-semibold text-gray-900">
                  {getLocalizedString(infoHabit.name, lang)}
                </span>
              </div>
              <button
                onClick={() => setInfoHabit(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {infoHabit.description && (
                <p className="text-gray-600">
                  {getLocalizedString(infoHabit.description, lang)}
                </p>
              )}
              <div className="flex flex-wrap gap-2 text-sm">
                {infoHabit.targetTime && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-lg flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {infoHabit.targetTime}
                  </span>
                )}
                {infoHabit.duration && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                    {infoHabit.duration} min
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
